import { useEffect, useMemo } from 'react';

import type { PDFSource } from '@/modules/pdf/PDFViewer';
import { PDFFilePage } from '@/modules/pdf/types';
import { convertGraphQLFilePages } from '@/modules/pdf/utils/filePageUtils';

import { useChatDocumentViewerDocumentQuery } from './graphql/ChatDocumentViewer.generated';

export interface CitationDocumentState {
  source: PDFSource | null;
  filePages: PDFFilePage[];
  /**
   * The document's first summary, for the viewer's Summary tab. Mirrors
   * HouseholdDocumentsViewer's fallback of showing the first summary when none
   * is explicitly selected; null when the document has no summaries.
   */
  summary: string | null;
  loading: boolean;
  error: boolean;
}

interface CitationDocumentLoaderProps {
  documentId: string;
  onStateChange: (documentId: string, state: CitationDocumentState) => void;
}

/**
 * Headless loader for a single document shown in the chat side content pane.
 * It resolves the document's PDF source and page geometry and reports them via
 * onStateChange — it renders nothing.
 *
 * Mounting one loader per document a citation references loads each document's
 * file ID and page data before the user steps between cited passages.
 */
export function CitationDocumentLoader({
  documentId,
  onStateChange,
}: CitationDocumentLoaderProps) {
  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useChatDocumentViewerDocumentQuery({
    variables: { documentId },
  });

  const documentNode = data?.node?.__typename === 'Document' ? data.node : null;
  // The pdfFile edge is always populated — the backend renders a PDF for every
  // document (and points pdfFile at the original when it is already a PDF), so
  // there is no separate file to fall back to.
  const file = documentNode?.pdfFile ?? null;
  const rawPages = documentNode?.pdfFile?.pages;
  const filePages = useMemo(
    () => convertGraphQLFilePages(rawPages),
    [rawPages]
  );
  const summary = documentNode?.summaries?.[0]?.summary ?? null;

  const fileId = file?.id;
  useEffect(() => {
    onStateChange(documentId, {
      source: fileId ? { kind: 'file', fileId } : null,
      filePages,
      summary,
      loading: queryLoading,
      error: Boolean(queryError),
    });
  }, [
    documentId,
    fileId,
    filePages,
    summary,
    queryLoading,
    queryError,
    onStateChange,
  ]);

  return null;
}
